import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ActivityFeed } from "../components/ActivityFeed.tsx";
import { ModeSelect, NewSessionDialog, slugify } from "../components/Dialogs.tsx";
import { EffortSelect, EnvFilesEditor, envFilesError, FolderPicker, ModelPicker } from "../components/Pickers.tsx";
import { SessionsTable } from "../components/SessionsTable.tsx";
import { Empty, ErrorBox, Field, Loading, Modal, Tabs, useAction, useToast } from "../components/ui.tsx";
import { del, errorText, patch, post, refresh, useApi } from "../lib/api.ts";
import type { ActivityItem, BotStatus, EnvFile, Project, Session, Settings } from "../lib/types.ts";
import { channelUrl } from "./Overview.tsx";

type Tab = "sessions" | "activity" | "settings";

export function ProjectPage() {
  const { slug = "" } = useParams();
  const project = useApi<Project>(`/api/projects/${slug}`);
  const sessions = useApi<Session[]>(`/api/projects/${slug}/sessions`);
  const settings = useApi<Settings>("/api/settings");
  const bot = useApi<BotStatus>("/api/discord");
  const [tab, setTab] = useState<Tab>("sessions");
  const [creating, setCreating] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [makeChannel, makingChannel] = useAction(async () => {
    await post(`/api/projects/${slug}/channel`);
    await project.mutate();
  }, "Channel created");

  if (!project.data) return project.error ? <ErrorBox error={project.error} /> : <Loading />;
  const p = project.data;
  const ch = channelUrl(settings.data?.["discord.guild_id"], p.channelId);

  return (
    <>
      <div className="pagehead">
        <div>
          <div className="muted small">
            <Link to="/projects">Projects</Link> /
          </div>
          <h1>{p.name}</h1>
          <div className="sub mono">{p.rootPath}</div>
          {p.kind === "workspace" && (
            <div className="row small">
              <span className="pill accent">workspace</span>
              {p.repos.map((r) => (
                <span key={r} className="pill mono">
                  {r}/
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="row">
          {ch ? (
            <a className="btn" href={ch} target="_blank" rel="noreferrer">
              Open #{p.slug} ↗
            </a>
          ) : (
            bot.data?.state === "ready" && (
              <button className="btn" disabled={makingChannel} onClick={() => makeChannel()}>
                Create Discord channel
              </button>
            )
          )}
          <button className="btn" onClick={() => setCloning(true)}>
            Clone…
          </button>
          <button className="btn primary" disabled={p.status !== "active"} onClick={() => setCreating(true)}>
            New session
          </button>
        </div>
      </div>
      <Tabs<Tab>
        value={tab}
        onChange={setTab}
        tabs={[
          { id: "sessions", label: `Sessions${sessions.data ? ` (${sessions.data.length})` : ""}` },
          { id: "activity", label: "Activity" },
          { id: "settings", label: "Settings" },
        ]}
      />
      {tab === "sessions" &&
        (sessions.data ? (
          sessions.data.length ? (
            <div className="card flush">
              <SessionsTable sessions={sessions.data} />
            </div>
          ) : (
            <div className="card">
              <Empty title="No sessions in this project yet">
                <button className="btn primary" onClick={() => setCreating(true)}>
                  Start a session
                </button>
              </Empty>
            </div>
          )
        ) : (
          <Loading />
        ))}
      {tab === "activity" && <ProjectActivity projectId={p.id} />}
      {tab === "settings" && (
        <>
          <ProjectSettings project={p} settings={settings.data} onSaved={(np) => project.mutate(np, { revalidate: false })} />
          <ProjectEnvFiles project={p} onSaved={(np) => project.mutate(np, { revalidate: false })} />
          <DangerZone project={p} sessionCount={sessions.data?.length} />
        </>
      )}
      {creating && <NewSessionDialog project={p} onClose={() => setCreating(false)} />}
      {cloning && <CloneProjectDialog project={p} root={(settings.data?.["server.projects_root"] as string | null) ?? ""} onClose={() => setCloning(false)} />}
    </>
  );
}

function ProjectActivity({ projectId }: { projectId: string }) {
  const activity = useApi<ActivityItem[]>(`/api/activity?project=${projectId}&limit=100`);
  if (!activity.data) return <Loading />;
  return (
    <div className="card">
      <ActivityFeed items={activity.data} showProject={false} />
    </div>
  );
}

const envToText = (env: Record<string, string>) =>
  Object.entries(env)
    .map(([k, v]) => `${k}=${v}`)
    .join("\n");

function textToEnv(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of text.split("\n")) {
    const t = line.trim();
    if (!t || t.startsWith("#")) continue;
    const eq = t.indexOf("=");
    if (eq > 0) out[t.slice(0, eq).trim()] = t.slice(eq + 1);
  }
  return out;
}

function ProjectSettings({ project, settings, onSaved }: { project: Project; settings?: Settings; onSaved: (p: Project) => void }) {
  const init = () => ({
    name: project.name,
    setupCmd: project.setupCmd ?? "",
    defaultModel: project.defaultModel ?? "",
    defaultEffort: project.defaultEffort ?? "",
    permissionMode: project.permissionMode,
    maxSessions: project.maxSessions,
    allowedTools: project.allowedTools.join("\n"),
    env: envToText(project.env),
    claudeMdTemplate: project.claudeMdTemplate ?? "",
  });
  const [form, setForm] = useState(init);
  useEffect(() => setForm(init()), [project.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = <K extends keyof ReturnType<typeof init>>(k: K, v: ReturnType<typeof init>[K]) => setForm((f) => ({ ...f, [k]: v }));
  const [save, saving] = useAction(async () => {
    const updated = await patch<Project>(`/api/projects/${project.id}`, {
      name: form.name.trim(),
      setupCmd: form.setupCmd.trim() || null,
      defaultModel: form.defaultModel.trim() || null,
      defaultEffort: form.defaultEffort || null,
      permissionMode: form.permissionMode,
      maxSessions: form.maxSessions,
      allowedTools: form.allowedTools
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean),
      env: textToEnv(form.env),
      ...(project.kind === "workspace" ? {} : { claudeMdTemplate: form.claudeMdTemplate.trim() ? form.claudeMdTemplate : null }),
    });
    onSaved(updated);
    refresh("/api/overview", "/api/projects");
  }, "Project saved. New sessions use these settings.");

  return (
    <form
      className="card"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <div className="grid2">
        <Field label="Name">
          <input value={form.name} onChange={(e) => set("name", e.target.value)} />
        </Field>
        <Field label="Setup command" hint="Runs in each new worktree before the session starts.">
          <input className="mono" value={form.setupCmd} placeholder="pnpm install" onChange={(e) => set("setupCmd", e.target.value)} />
        </Field>
        <Field label="Default model">
          <ModelPicker
            value={form.defaultModel}
            onChange={(v) => set("defaultModel", v)}
            inheritLabel={`Settings default (${(settings?.["claude.default_model"] as string | null) || "Claude Code's default"})`}
          />
        </Field>
        <Field label="Effort">
          <EffortSelect
            value={form.defaultEffort}
            onChange={(v) => set("defaultEffort", v)}
            inheritLabel={`Settings default (${(settings?.["claude.default_effort"] as string | null) || "model default"})`}
          />
        </Field>
        <Field label="Permission mode">
          <ModeSelect value={form.permissionMode} onChange={(v) => set("permissionMode", v)} />
        </Field>
        <Field label="Max concurrent sessions">
          <input type="number" min={1} max={32} value={form.maxSessions} onChange={(e) => set("maxSessions", Number(e.target.value) || 1)} />
        </Field>
        <Field label="Allowed tools" hint="One per line, e.g. Bash(npm test:*). Allowed without asking.">
          <textarea className="mono" rows={3} value={form.allowedTools} onChange={(e) => set("allowedTools", e.target.value)} />
        </Field>
      </div>
      <Field label="Environment variables" hint="KEY=value, one per line. Passed to the setup command and the session.">
        <textarea className="mono" rows={4} value={form.env} placeholder="NODE_ENV=development" onChange={(e) => set("env", e.target.value)} />
      </Field>
      {project.kind === "workspace" ? (
        <div className="muted small">
          The workspace rules live in <code>{project.rootPath}/CLAUDE.md</code>, committed to the workspace repository. Edit and commit it there; new sessions pick it up.
        </div>
      ) : (
        <Field label="CLAUDE.md template" hint="Written into new worktrees that have no CLAUDE.md. Leave empty for none.">
          <textarea className="mono" rows={6} value={form.claudeMdTemplate} onChange={(e) => set("claudeMdTemplate", e.target.value)} />
        </Field>
      )}
      <div className="row">
        <button className="btn primary" type="submit" disabled={saving || !form.name.trim()}>
          Save
        </button>
        <button className="btn ghost" type="button" onClick={() => setForm(init())}>
          Reset
        </button>
      </div>
    </form>
  );
}

function ProjectEnvFiles({ project, onSaved }: { project: Project; onSaved: (p: Project) => void }) {
  const stored = useApi<EnvFile[]>(`/api/projects/${project.id}/env-files`, { revalidateOnFocus: false });
  const [files, setFiles] = useState<EnvFile[] | null>(null);
  const current = files ?? stored.data ?? [];
  const problem = envFilesError(current);
  const [save, saving] = useAction(async () => {
    const updated = await patch<Project>(`/api/projects/${project.id}`, {
      envFiles: current.map((f) => ({ repo: f.repo, path: f.path.trim(), content: f.content })),
    });
    await stored.mutate(current, { revalidate: false });
    setFiles(null);
    onSaved(updated);
  }, "Env files saved. New sessions get them; existing worktrees keep theirs.");

  if (!stored.data) return stored.error ? <ErrorBox error={stored.error} /> : <Loading />;
  return (
    <section className="card">
      <EnvFilesEditor files={current} onChange={setFiles} repos={project.kind === "workspace" ? project.repos : []} />
      {problem && <div className="callout bad">{problem}</div>}
      <div className="row">
        <button className="btn primary" disabled={saving || files === null || Boolean(problem)} onClick={() => save()}>
          Save env files
        </button>
        <button className="btn ghost" disabled={files === null} onClick={() => setFiles(null)}>
          Reset
        </button>
      </div>
    </section>
  );
}

const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,62}$/;

/** Copies the project's folder somewhere else under the projects root and opens the copy as a new project. */
function CloneProjectDialog({ project, root, onClose }: { project: Project; root: string; onClose: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [name, setName] = useState(`${project.name} copy`);
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [path, setPath] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const folder = path.trim() || effectiveSlug;
  const slugOk = SLUG_RE.test(effectiveSlug);
  const rootJoin = (p: string) => `${root}${root.endsWith("/") || root.endsWith("\\") ? "" : "/"}${p}`;

  const clone = async () => {
    setBusy(true);
    setError(null);
    try {
      const copy = await post<Project & { channelError?: string }>(`/api/projects/${project.id}/clone`, { name: name.trim(), slug: effectiveSlug, path: folder });
      refresh("/api/projects", "/api/overview", "/api/activity");
      if (copy.channelError) toast(`Cloned to ${copy.name}, but the Discord channel failed: ${copy.channelError}`, "bad");
      else toast(copy.channelId ? `Cloned to ${copy.name} and #${copy.slug}` : `Cloned to ${copy.name}`);
      onClose();
      navigate(`/projects/${copy.slug}`);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Clone ${project.name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={busy || !name.trim() || !slugOk || !folder} onClick={() => void clone()}>
            {busy ? "Copying…" : "Clone project"}
          </button>
        </>
      }
    >
      <div className="muted small">
        Copies <code>{project.rootPath}</code> with every file, branch and uncommitted change into a new folder, and adds it as a new project with the same
        settings and env files. Sessions, their history and session worktrees are not copied.
      </div>
      <div className="grid2">
        <Field label="Project name">
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={`${project.name} copy`} />
        </Field>
        <Field label="Slug · channel name" error={effectiveSlug && !slugOk ? "lowercase letters, digits and dashes" : undefined}>
          <input
            className="mono"
            value={effectiveSlug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value);
            }}
            placeholder={`${project.slug}-copy`}
          />
        </Field>
      </div>
      <FolderPicker
        label="Copy to (relative to the projects root)"
        value={path}
        onChange={setPath}
        placeholder={effectiveSlug || `${project.slug}-copy`}
        hint={root ? `Resolves to ${rootJoin(folder || "<slug>")}. Must be a new or empty folder.` : "Must be a new or empty folder."}
      />
      {error && <div className="callout bad">{error}</div>}
    </Modal>
  );
}

function DangerZone({ project, sessionCount }: { project: Project; sessionCount?: number }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="card" style={{ borderColor: "var(--bad)" }}>
      <header>
        <div className="stack tight">
          <h3>Delete project</h3>
          <div className="muted small">
            Stops its sessions and removes the project, its sessions and their history from Switchboard, and deletes{" "}
            {project.channelId ? <code>#{project.slug}</code> : "its channel"} with every session thread from Discord.
          </div>
        </div>
        <button className="btn danger" onClick={() => setOpen(true)}>
          Delete project…
        </button>
      </header>
      {open && <DeleteProjectDialog project={project} sessionCount={sessionCount} onClose={() => setOpen(false)} />}
    </section>
  );
}

function DeleteProjectDialog({ project, sessionCount, onClose }: { project: Project; sessionCount?: number; onClose: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [typed, setTyped] = useState("");
  const [deleteFiles, setDeleteFiles] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await del<{ deleted: string; channelError?: string }>(`/api/projects/${project.id}`, { deleteFiles });
      refresh("/api/projects", "/api/overview", "/api/sessions", "/api/activity");
      if (r.channelError) toast(`Deleted ${project.name}, but ${r.channelError}`, "bad");
      else toast(`Deleted ${project.name}${project.channelId ? ` and #${project.slug}` : ""}`);
      onClose();
      navigate("/projects");
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Delete ${project.name}?`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn danger solid" disabled={busy || typed !== project.slug} onClick={() => void remove()}>
            {busy ? "Deleting…" : "Delete project"}
          </button>
        </>
      }
    >
      <div className="stack tight small">
        <div>This cannot be undone. It will:</div>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          <li>stop {sessionCount === undefined ? "its" : sessionCount === 1 ? "its 1" : `all ${sessionCount}`} session{sessionCount === 1 ? "" : "s"} and remove their worktrees (uncommitted changes in them are lost)</li>
          <li>delete the project, its sessions, transcripts and env files from Switchboard</li>
          <li>{project.channelId ? <>delete <code>#{project.slug}</code> and all its threads from Discord</> : "(it has no Discord channel)"}</li>
        </ul>
        <div className="muted">
          Session branches stay in the {project.kind === "workspace" ? "repositories" : "repository"} at <code>{project.rootPath}</code> unless you delete the folder too.
        </div>
      </div>
      <label className="check">
        <input type="checkbox" checked={deleteFiles} onChange={(e) => setDeleteFiles(e.target.checked)} />
        <span>
          Also delete the folder <code>{project.rootPath}</code> from the server, with every repository in it and any work that was not pushed
        </span>
      </label>
      <Field label={`Type ${project.slug} to confirm`}>
        <input className="mono" autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={project.slug} />
      </Field>
      {error && <div className="callout bad">{error}</div>}
    </Modal>
  );
}
